import axios from 'axios';

// ─────────────────────────────────────────────────────────────────────────────
// AXIOS BASE INSTANCE
// ─────────────────────────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://civilfox-backend.onrender.com/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// ─────────────────────────────────────────────────────────────────────────────
// PRELIMS PDF DOWNLOAD
// Question-day D = 06:00 IST on D → 06:00 IST on D+1 (same rule as the backend's
// utils/questionDay.js). A day's PDF contains ONLY questions generated inside
// that window — never another day, never a mix. 'all' is an explicit archive
// request and is never used by the per-day button.
//
// Questions go to ./prelimsPdf.js, which detects each question's type and lays
// it out like the real UPSC paper. jsPDF is loaded lazily inside renderPrelimsPdf.
// ─────────────────────────────────────────────────────────────────────────────
const DAY_START_HOUR_IST = 6; // keep in sync with backend utils/questionDay.js

function questionDayWindow(dateStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr || '').trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const check = new Date(Date.UTC(y, mo - 1, d));
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) return null;
  const from = Date.UTC(y, mo - 1, d, DAY_START_HOUR_IST) - 5.5 * 60 * 60 * 1000;
  return { from: new Date(from), to: new Date(from + 24 * 60 * 60 * 1000) };
}

// Safety net: the server stamps every question with `generatedAt` (the exact timestamp it matched
// on). Drop anything outside the window. Other date fields are ignored on purpose — e.g. a news
// question's publishedAt can be the article's date, not when the question was generated.
const stampOf = (q) => {
  const t = new Date(q.generatedAt || NaN).getTime();
  return Number.isNaN(t) ? null : t;
};
function keepInsideWindow(questions, win) {
  const lo = win.from.getTime();
  const hi = win.to.getTime();
  const kept = questions.filter((q) => {
    const t = stampOf(q);
    return t === null || (t >= lo && t < hi);
  });
  if (kept.length !== questions.length) {
    console.warn(`prelims-paper: dropped ${questions.length - kept.length} question(s) generated outside the selected day`);
  }
  if (questions.length && questions.every((q) => stampOf(q) === null)) {
    console.warn('prelims-paper: server sent no generation timestamps, so day filtering relies on the server');
  }
  return kept;
}

export async function downloadPrelimsPdf({ date = 'today', answerSheet = false } = {}) {
  const requestedDate = date === 'today'
    ? new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date())
    : date;

  const win = requestedDate === 'all' ? null : questionDayWindow(requestedDate);
  if (requestedDate !== 'all' && !win) {
    throw new Error(`Invalid date "${requestedDate}" — expected YYYY-MM-DD.`);
  }

  const response = await api.get('/public/prelims-paper', {
    params: {
      date: requestedDate,
      answers: answerSheet ? '1' : '0',
      ...(win && { from: win.from.toISOString(), to: win.to.toISOString() }),
    },
  });
  let questions = response.data.questions || [];
  if (win) questions = keepInsideWindow(questions, win);
  if (!questions.length) throw new Error('No Prelims questions were generated for this day yet.');

  const { renderPrelimsPdf } = await import('./prelimsPdf');
  const doc = await renderPrelimsPdf({ questions, requestedDate, answerSheet });

  const dateSuffix = requestedDate === 'all' ? 'all-questions' : requestedDate;
  doc.save(`civilfox-upsc-prelims-${dateSuffix}${answerSheet ? '-answer-sheet' : ''}.pdf`);
  return questions.length;
}

export const editorialsAPI = {
  getEditorials: () => api.get('/editorials'),
};
// ─────────────────────────────────────────────────────────────────────────────
// AUTH TOKEN INJECTION
// ─────────────────────────────────────────────────────────────────────────────
api.interceptors.request.use(config => {
  const token = localStorage.getItem('vidyacards_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─────────────────────────────────────────────────────────────────────────────
// GLOBAL ERROR HANDLING
// ─────────────────────────────────────────────────────────────────────────────
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('vidyacards_token');
      localStorage.removeItem('vidyacards_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// AUTH APIs
// ─────────────────────────────────────────────────────────────────────────────
export const authAPI = {
  sendOTP: (data) => api.post('/auth/send-otp', data),
  verifyOTP: (data) => api.post('/auth/verify-otp', data),
  resendOTP: (email) => api.post('/auth/resend-otp', { email }),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  deleteAccount: (data) => api.delete('/auth/account', { data }),

  // ← NEW: Forgot password flow (3 steps)
  forgotPassword: (data) => api.post('/auth/forgot-password', data),   // { email }
  verifyResetOtp: (data) => api.post('/auth/verify-reset-otp', data),  // { email, otp }
  resetPassword:  (data) => api.post('/auth/reset-password', data),   // { email, resetToken, newPassword }
};

// ─────────────────────────────────────────────────────────────────────────────
// CARDS APIs (LEARNING SYSTEM)
// ─────────────────────────────────────────────────────────────────────────────
export const cardsAPI = {
  getAll: (params) => api.get('/cards', { params }),
  getOne: (id) => api.get(`/cards/${id}`),
  getSubjectStats: () => api.get('/study/subject-stats'), 
  getRecentNews: () => api.get('/cards/recent-news'),
  bookmark: (id) => api.post(`/cards/${id}/bookmark`),
  create: (data) => api.post('/cards', data)
};

// ─────────────────────────────────────────────────────────────────────────────
// STUDY SYSTEM APIs (SPACED REPETITION)
// ─────────────────────────────────────────────────────────────────────────────
// A stalled review request must fail, not hang: the review outbox retries failures,
// and the server applies a repeated reviewId only once, so a timeout is safe.
const REVIEW_TIMEOUT_MS = 30000;

export const studyAPI = {
  getDue: (params) => api.get('/study/due', { params }),
  getDueSubjects: (params) => api.get('/study/due/subjects', { params }),  // ← ADD THIS
  getQuestionDays: (params) => api.get('/study/question-days', { params }),
  getQuestionBank: (params) => api.get('/study/question-bank', { params }),
  getRememberProgress: (params) => api.get('/study/remember-progress', { params }),
  markRememberDone: (data) => api.post('/study/remember-progress', data),
  submitReview: (data) => api.post('/study/review', data, { timeout: REVIEW_TIMEOUT_MS }),
  getStats: () => api.get('/study/stats'),
  getBookmarks: () => api.get('/study/bookmarks')
};
// ─────────────────────────────────────────────────────────────────────────────
// PYQ SYSTEM (PREVIOUS YEAR QUESTIONS)
// ─────────────────────────────────────────────────────────────────────────────
export const pyqAPI = {
  list: (params) => api.get('/pyq', { params }),
  search: (q) => api.get('/public/search-questions', { params: { q } }),
  create: (data) => api.post('/pyq', data),
  update: (id, data) => api.put(`/pyq/${id}`, data),
  delete: (id) => api.delete(`/pyq/${id}`)
};

// ─────────────────────────────────────────────────────────────────────────────
// NEWS SYSTEM (REAL-TIME UPSC FEED + AI EXPLANATION ENGINE)
// ─────────────────────────────────────────────────────────────────────────────
export const newsAPI = {
  // LIVE FEED
  getLive: (params) => api.get('/news/live', { params }),

  // CORE GENERATION
  generate: () => api.post('/news/generate'),
  generateTopic: (data) => api.post('/news/generate-topic', data),

  // AI EXPLANATION SYSTEM
  explain: (data) => api.post('/news/explain', data),

  // SINGLE ARTICLE EXPLANATION FETCH
  getExplanation: (id) => api.get(`/news/explain/${id}`),

  // REAL-TIME CONTROL ENDPOINTS
  triggerFetch: () => api.post('/news/fetch'),
  triggerExplain: () => api.post('/news/explain-all')
};

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN SYSTEM (FULL CONTROL PANEL)
// ─────────────────────────────────────────────────────────────────────────────
export const adminAPI = {
  // DASHBOARD
  getStats: () => api.get('/admin/stats'),

  // USER MANAGEMENT
  getUsers: (params) => api.get('/admin/users', { params }),
  setRole: (id, role) => api.put(`/admin/users/${id}/role`, { role }),

  // CONTENT MANAGEMENT
  addCard: (data) => api.post('/admin/cards', data),
  editCard: (id, data) => api.put(`/admin/cards/${id}`, data),
  deleteCard: (id) => api.delete(`/admin/cards/${id}`)
};

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT DEFAULT INSTANCE
// ─────────────────────────────────────────────────────────────────────────────
export default api;